import { Graphics, Container, ContainerChild } from "pixi.js";
import type {
  IStudioWidget,
  IStudioWidgetAudioVisualizer,
  
} from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";
import { EAudioVisualizerSource } from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";
import { AudioVisualizerService, type AudioDataCallback } from "../Services/AudioVisualizerService";

interface SourceSubscription {
  source: EAudioVisualizerSource | string;
  callback: AudioDataCallback;
}

// Draw interval — 20fps is sufficient for audio visualization and frees up the main thread
const DRAW_INTERVAL_MS = 50;

export class AudioVisualizerWidgetRenderer {
  private graphicsObjects = new Map<string, Graphics>();
  private subscriptions = new Map<string, SourceSubscription[]>();
  private sourceDataBuffers = new Map<string, Uint8Array>();
  /** Active draw timers per widget — cancelled on re-render and destroy */
  private drawTimers = new Map<string, ReturnType<typeof setTimeout>>();

  // Pre-allocated buffers to avoid per-frame GC pressure (Case 2)
  private _ptsX = new Float32Array(256);
  private _ptsY = new Float32Array(256);
  private _drawEntries: { data: Uint8Array; color: string }[] = [];

  render(
    widget: IStudioWidget,
    container: Container,
    destination: { x: number; y: number; width: number; height: number }
  ): void {
    const options = (widget as IStudioWidgetAudioVisualizer).options;
    const widgetKey = `audio_viz_${widget.id}`;

    // Cancel any existing draw loop for this widget before starting a new one
    this.stopDrawLoop(widgetKey);

    // Draw background
    if (options.backgroundColor && options.backgroundColor !== "transparent") {
      const bg = new Graphics();
      bg.rect(0, 0, destination.width, destination.height).fill(options.backgroundColor);
      container.addChild(bg as unknown as ContainerChild);
    }

    // Create graphics object for visualization
    const vizGraphics = new Graphics();
    container.addChild(vizGraphics as unknown as ContainerChild);
    this.graphicsObjects.set(widgetKey, vizGraphics);

    const service = AudioVisualizerService.getInstance();

    // Clean up previous subscriptions
    this.cleanupSubscriptions(widgetKey);

    const enabledSources = options.sources.filter((s) => s.enabled);

    const connectSources = () => {
      // Clean previous
      this.cleanupSubscriptions(widgetKey);
      const subs: SourceSubscription[] = [];

      for (const srcConfig of enabledSources) {
        // For VIDEO_AREA, auto-detect all connected videos
        if (srcConfig.source === EAudioVisualizerSource.VIDEO_AREA) {
          const videoIds = service.getConnectedVideoIds();
          for (const videoId of videoIds) {
            const key = `${EAudioVisualizerSource.VIDEO_AREA}::${videoId}`;
            service.connect(key, options.fftSize, options.smoothing);

            const bufferKey = `${widgetKey}_${key}`;
            const callback: AudioDataCallback = (data, bufferLength) => {
              if (!this.sourceDataBuffers.has(bufferKey)) {
                this.sourceDataBuffers.set(bufferKey, new Uint8Array(bufferLength));
              }
              this.sourceDataBuffers.get(bufferKey)!.set(data);
            };

            subs.push({ source: key, callback });
            service.subscribe(key, callback);
          }
          continue;
        }

        service.connect(srcConfig.source, options.fftSize, options.smoothing);

        const bufferKey = `${widgetKey}_${srcConfig.source}`;
        const callback: AudioDataCallback = (data, bufferLength) => {
          if (!this.sourceDataBuffers.has(bufferKey)) {
            this.sourceDataBuffers.set(bufferKey, new Uint8Array(bufferLength));
          }
          this.sourceDataBuffers.get(bufferKey)!.set(data);
        };

        subs.push({ source: srcConfig.source, callback });
        service.subscribe(srcConfig.source, callback);
      }

      this.subscriptions.set(widgetKey, subs);
    };

    connectSources();

    // setTimeout-based draw loop (not rAF) to avoid competing with PixiJS rendering.
    let redetectCounter = 0;
    const drawFrame = () => {
      if (!vizGraphics || vizGraphics.destroyed) {
        this.drawTimers.delete(widgetKey);
        return;
      }

      // Re-detect video sources every ~60 draws (~3s at 20fps)
      const hasVideoSource = enabledSources.some(
        (s) => s.source === EAudioVisualizerSource.VIDEO_AREA
      );
      if (hasVideoSource && ++redetectCounter >= 60) {
        redetectCounter = 0;
        const currentSubs = this.subscriptions.get(widgetKey) || [];
        const currentVideoKeys = currentSubs
          .filter(
            (s) =>
              typeof s.source === "string" &&
              s.source.startsWith(`${EAudioVisualizerSource.VIDEO_AREA}::`)
          )
          .map((s) => s.source as string);
        const liveKeys = service
          .getConnectedVideoIds()
          .map((id) => `${EAudioVisualizerSource.VIDEO_AREA}::${id}`);

        const hasChanges =
          liveKeys.length !== currentVideoKeys.length ||
          liveKeys.some((k) => !currentVideoKeys.includes(k));
        if (hasChanges) {
          connectSources();
        }
      }

      vizGraphics.clear();

      // Build draw list reusing pre-allocated array to avoid per-frame allocations
      const drawEntries = this._drawEntries;
      let drawCount = 0;

      for (const srcConfig of enabledSources) {
        if (srcConfig.source === EAudioVisualizerSource.VIDEO_AREA) {
          for (const [bKey, buf] of this.sourceDataBuffers) {
            if (bKey.startsWith(`${widgetKey}_${EAudioVisualizerSource.VIDEO_AREA}::`)) {
              if (buf.some((v) => v > 0)) {
                if (drawCount < drawEntries.length) {
                  drawEntries[drawCount].data = buf;
                  drawEntries[drawCount].color = srcConfig.color;
                } else {
                  drawEntries.push({ data: buf, color: srcConfig.color });
                }
                drawCount++;
              }
            }
          }
          continue;
        }

        const bufferKey = `${widgetKey}_${srcConfig.source}`;
        const data = this.sourceDataBuffers.get(bufferKey);
        if (data && data.some((v) => v > 0)) {
          if (drawCount < drawEntries.length) {
            drawEntries[drawCount].data = data;
            drawEntries[drawCount].color = srcConfig.color;
          } else {
            drawEntries.push({ data, color: srcConfig.color });
          }
          drawCount++;
        }
      }

      for (let si = 0; si < drawCount; si++) {
        const entry = drawEntries[si];

        this.drawAreaChart(
          vizGraphics,
          entry.data,
          entry.data.length,
          destination,
          options,
          entry.color,
          si,
          drawCount
        );
      }

      this.drawTimers.set(widgetKey, setTimeout(drawFrame, DRAW_INTERVAL_MS));
    };

    this.drawTimers.set(widgetKey, setTimeout(drawFrame, DRAW_INTERVAL_MS));
  }

  private stopDrawLoop(widgetKey: string): void {
    const timerId = this.drawTimers.get(widgetKey);
    if (timerId !== undefined) {
      clearTimeout(timerId);
      this.drawTimers.delete(widgetKey);
    }
  }

  private drawAreaChart(
    g: Graphics,
    data: Uint8Array,
    bufferLength: number,
    dest: { width: number; height: number },
    options: IStudioWidgetAudioVisualizer["options"],
    color: string,
    _sourceIndex: number = 0,
    _totalSources: number = 1
  ): void {
    const points = Math.min(options.barCount || 64, bufferLength);
    const step = Math.floor(bufferLength / points);
    const sliceWidth = dest.width / Math.max(1, points - 1);

    // Reuse pre-allocated Float32Arrays instead of creating {x,y}[] each frame
    if (this._ptsX.length < points) {
      this._ptsX = new Float32Array(points);
      this._ptsY = new Float32Array(points);
    }
    const ptsX = this._ptsX;
    const ptsY = this._ptsY;
    for (let i = 0; i < points; i++) {
      const value = data[i * step] / 255;
      ptsX[i] = i * sliceWidth;
      ptsY[i] = dest.height - value * dest.height;
    }

    if (points < 2) return;

    const tension = 0.3;

    // --- Gradient-filled area using horizontal strips ---
    const stripCount = 8;
    const stripHeight = dest.height / stripCount;

    const lastPt = points - 1;

    for (let s = 0; s < stripCount; s++) {
      const stripTop = s * stripHeight;
      const stripBottom = stripTop + stripHeight;
      const alpha = ((s + 1) / stripCount) * 0.45;

      g.moveTo(ptsX[0], Math.max(ptsY[0], stripTop));

      for (let i = 0; i < lastPt; i++) {
        const i0 = Math.max(0, i - 1);
        const i3 = Math.min(lastPt, i + 2);

        const cp1x = ptsX[i] + (ptsX[i + 1] - ptsX[i0]) * tension;
        const cp1y = Math.min(
          Math.max(ptsY[i] + (ptsY[i + 1] - ptsY[i0]) * tension, stripTop),
          stripBottom
        );
        const cp2x = ptsX[i + 1] - (ptsX[i3] - ptsX[i]) * tension;
        const cp2y = Math.min(
          Math.max(ptsY[i + 1] - (ptsY[i3] - ptsY[i]) * tension, stripTop),
          stripBottom
        );
        const endY = Math.min(Math.max(ptsY[i + 1], stripTop), stripBottom);

        g.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, ptsX[i + 1], endY);
      }

      g.lineTo(ptsX[lastPt], stripBottom);
      g.lineTo(ptsX[0], stripBottom);
      g.closePath();
      g.fill({ color, alpha });
    }

    // --- Stroke line on top ---
    g.moveTo(ptsX[0], ptsY[0]);
    for (let i = 0; i < lastPt; i++) {
      const i0 = Math.max(0, i - 1);
      const i3 = Math.min(lastPt, i + 2);

      const cp1x = ptsX[i] + (ptsX[i + 1] - ptsX[i0]) * tension;
      const cp1y = ptsY[i] + (ptsY[i + 1] - ptsY[i0]) * tension;
      const cp2x = ptsX[i + 1] - (ptsX[i3] - ptsX[i]) * tension;
      const cp2y = ptsY[i + 1] - (ptsY[i3] - ptsY[i]) * tension;

      g.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, ptsX[i + 1], ptsY[i + 1]);
    }
    g.stroke({ width: 2, color });

    if (options.mirror) {
      const dh = dest.height;

      for (let s = 0; s < stripCount; s++) {
        const stripTop = s * stripHeight;
        const stripBottom = stripTop + stripHeight;
        const alpha = (1 - s / stripCount) * 0.45;

        g.moveTo(ptsX[0], Math.min(dh - ptsY[0], stripBottom));

        for (let i = 0; i < lastPt; i++) {
          const i0 = Math.max(0, i - 1);
          const i3 = Math.min(lastPt, i + 2);

          const cp1x = ptsX[i] + (ptsX[i + 1] - ptsX[i0]) * tension;
          const cp1y = Math.min(
            Math.max(dh - (ptsY[i] + (ptsY[i + 1] - ptsY[i0]) * tension), stripTop),
            stripBottom
          );
          const cp2x = ptsX[i + 1] - (ptsX[i3] - ptsX[i]) * tension;
          const cp2y = Math.min(
            Math.max(dh - (ptsY[i + 1] - (ptsY[i3] - ptsY[i]) * tension), stripTop),
            stripBottom
          );
          const endY = Math.min(Math.max(dh - ptsY[i + 1], stripTop), stripBottom);

          g.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, ptsX[i + 1], endY);
        }

        g.lineTo(ptsX[lastPt], stripTop);
        g.lineTo(ptsX[0], stripTop);
        g.closePath();
        g.fill({ color, alpha });
      }

      // Mirrored stroke line
      g.moveTo(ptsX[0], dh - ptsY[0]);
      for (let i = 0; i < lastPt; i++) {
        const i0 = Math.max(0, i - 1);
        const i3 = Math.min(lastPt, i + 2);

        const cp1x = ptsX[i] + (ptsX[i + 1] - ptsX[i0]) * tension;
        const cp1y = dh - (ptsY[i] + (ptsY[i + 1] - ptsY[i0]) * tension);
        const cp2x = ptsX[i + 1] - (ptsX[i3] - ptsX[i]) * tension;
        const cp2y = dh - (ptsY[i + 1] - (ptsY[i3] - ptsY[i]) * tension);

        g.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, ptsX[i + 1], dh - ptsY[i + 1]);
      }
      g.stroke({ width: 2, color });
    }
  }

  private cleanupSubscriptions(widgetKey: string): void {
    const service = AudioVisualizerService.getInstance();
    const existing = this.subscriptions.get(widgetKey);
    if (existing) {
      for (const sub of existing) {
        service.unsubscribe(sub.source, sub.callback);
      }
    }
    this.subscriptions.delete(widgetKey);

    // Clean up data buffers for this widget
    for (const key of this.sourceDataBuffers.keys()) {
      if (key.startsWith(widgetKey)) {
        this.sourceDataBuffers.delete(key);
      }
    }
  }

  public destroy(): void {
    // Stop all draw loops
    for (const [widgetKey] of this.drawTimers) {
      this.stopDrawLoop(widgetKey);
    }
    for (const [widgetKey] of this.subscriptions) {
      this.cleanupSubscriptions(widgetKey);
    }
    this.subscriptions.clear();
    this.graphicsObjects.clear();
    this.sourceDataBuffers.clear();
    this.drawTimers.clear();
  }
}
